import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  List as ListIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import FormBuilder from '../components/forms/FormBuilder';

const FormBuilderPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(1); // Default to form builder tab
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [isNewForm, setIsNewForm] = useState(true); // Assume new form by default
  const [formTitle, setFormTitle] = useState('New Event Form');
  const [formDescription, setFormDescription] = useState('Please fill out this form to register for the event.');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // console.log('FormBuilderPage mounted with eventId:', eventId);

  // Check if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      // console.error('No authentication token found');
      setError('You must be logged in to access the form builder');
      // Optionally redirect to login
      // navigate('/login', { state: { from: `/form-builder/${eventId}` } });
    }
  }, [eventId]);

  // Create a new empty form
  const createNewForm = () => {
    return {
      id: `temp-${Date.now()}`,
      title: 'New Event Form',
      description: 'Please fill out this form to register for the event.',
      event: parseInt(eventId),
      sections: [],
      is_active: true,
      is_published: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  };

  // Initialize the form builder with a new form if none exists
  const initializeFormBuilder = () => {
    if (!initialized) {
      // console.log('Initializing form builder with new form');
      const newForm = createNewForm();
      setSelectedForm(newForm);
      setFormTitle(newForm.title);
      setFormDescription(newForm.description);
      setIsNewForm(true);
      setInitialized(true);
      setLoading(false);
    }
  };

  // Fetch all forms for the event
  const fetchForms = async () => {
    try {
      // console.log('Fetching forms for event:', eventId);
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const url = `${config.API_BASE_URL}/forms/event-forms/?event_id=${eventId}`;
      // console.log('Fetching from URL:', url);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // console.log('Forms API response:', response.data);
      
      const formsData = response.data.results || [];
      setForms(formsData);
      
      if (formsData.length > 0) {
        // console.log('Found forms, selecting first one:', formsData[0]);
        setSelectedForm(formsData[0]);
        setFormTitle(formsData[0].title);
        setFormDescription(formsData[0].description || '');
        setIsNewForm(false);
      } else {
        // console.log('No forms found, initializing new form');
        initializeFormBuilder();
      }
    } catch (error) {
      // console.error('Error loading forms:', error);
      const errorMessage = error.response 
        ? `Server responded with ${error.response.status}: ${error.response.data?.detail || 'Unknown error'}`
        : error.message;
      // console.error('Error details:', errorMessage);
      setError(`Failed to load forms: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Initialize form title and description when selectedForm changes
  useEffect(() => {
    if (selectedForm) {
      setFormTitle(selectedForm.title || 'New Event Form');
      setFormDescription(selectedForm.description || '');
    }
  }, [selectedForm]);

  // Load forms when component mounts or eventId changes
  useEffect(() => {
    if (eventId) {
      fetchForms().catch(err => {
        // console.error('Error fetching forms, initializing with new form', err);
        initializeFormBuilder();
      });
    }
  }, [eventId]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle creating a new form
  const handleNewForm = () => {
    // console.log('Creating new form for event:', eventId);
    const newForm = createNewForm();
    
    // console.log('New form data:', newForm);
    
    // Update the forms list with the new form
    setForms(prevForms => [...prevForms, newForm]);
    
    // Set the new form as selected
    setSelectedForm(newForm);
    setFormTitle(newForm.title);
    setFormDescription(newForm.description);
    setIsNewForm(true);
    setActiveTab(1); // Switch to the form builder tab
    
    // Clear any previous errors
    setError(null);
  };

  // Handle saving a form
  const handleSaveForm = async (formData) => {
    try {
      setLoadingSave(true);
      const token = localStorage.getItem('access_token');
      const formToSave = {
        ...formData,
        title: formTitle,
        description: formDescription,
        event: parseInt(eventId)
      };

      const url = formData.id 
        ? `${config.API_BASE_URL}/event-forms/${formData.id}/`
        : `${config.API_BASE_URL}/event-forms/`;
      
      const method = formData.id ? 'put' : 'post';
      
      const response = await axios({
        method,
        url,
        data: formToSave,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // Refresh the forms list
      await fetchForms();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Form saved successfully!',
        severity: 'success'
      });
      
      return response.data;
    } catch (error) {
      // console.error('Error saving form:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save form. Please try again.',
        severity: 'error'
      });
      throw error;
    } finally {
      setLoadingSave(false);
      setShowSaveDialog(false);
    }
  };

  // Handle form selection
  const handleSelectForm = (form) => {
    setSelectedForm(form);
    setActiveTab(1); // Switch to the form builder tab
  };

  // Handle form deletion
  const handleDeleteForm = (form) => {
    setFormToDelete(form);
    setShowDeleteConfirm(true);
  };

  // Confirm form deletion
  const confirmDeleteForm = async () => {
    if (!formToDelete) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      await axios.delete(`${config.API_BASE_URL}/forms/event-forms/${formToDelete.id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Refresh the forms list
      await fetchForms();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Form deleted successfully!',
        severity: 'success'
      });
      
      // If we deleted the currently selected form, clear the selection
      if (selectedForm && formToDelete.id === selectedForm.id) {
        setSelectedForm(null);
        setIsNewForm(false);
      }
    } catch (error) {
      // console.error('Error deleting form:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete form. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setFormToDelete(null);
    }
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle going back
  const handleBack = () => {
    navigate(-1);
  };

  // Render loading state - only show if we don't have a form yet
  if (loading && !selectedForm) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
          <CircularProgress />
          <Typography variant="h6" mt={2}>
            Setting up form builder...
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={1}>
            Event ID: {eventId}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: 600 }}>
              {error}
            </Alert>
          )}
        </Box>
      </Container>
    );
  }

  // Render empty state when no form is selected
  if (!selectedForm && forms.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box textAlign="center" mt={8}>
          <Typography variant="h5" gutterBottom>
            No forms found for this event
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Get started by creating a new form for your event.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleNewForm}
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
          >
            Create New Form
          </Button>
        </Box>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Paper>
      </Container>
    );
  }

  // console.log('Rendering FormBuilderPage with state:', {
  //   loading,
  //   error,
  //   activeTab,
  //   formsCount: forms.length,
  //   selectedForm: selectedForm ? 'Form selected' : 'No form selected',
  //   isNewForm,
  //   formTitle,
  //   formDescription
  // });

  // Debug information
  const debugInfo = {
    loading,
    error,
    eventId,
    formsCount: forms.length,
    selectedForm: selectedForm ? {
      id: selectedForm.id,
      title: selectedForm.title,
      sections: selectedForm.sections ? selectedForm.sections.length : 0
    } : null,
    isNewForm,
    formTitle,
    formDescription,
    activeTab
  };

  // console.log('FormBuilderPage render state:', debugInfo);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Debug panel - can be toggled with a button if needed */}
      <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={1}>
        <Typography variant="subtitle2" color="textSecondary">Debug Info:</Typography>
        <pre style={{ fontSize: '0.75rem', overflowX: 'auto' }}>
          {JSON.stringify(debugInfo, null, 2)}
        </pre>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          color="inherit"
          sx={{ mb: 2 }}
        >
          Back to Event
        </Button>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {selectedForm?.id ? 'Edit Registration Form' : 'Create Registration Form'}
            </Typography>
            <Typography color="textSecondary" paragraph>
              Build your event registration form by adding sections and fields.
              Drag and drop to reorder sections and fields as needed.
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNewForm}
            disabled={loadingSave}
          >
            New Form
          </Button>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
          sx={{ mb: 3 }}
        >
          <Tab icon={<ListIcon />} label="My Forms" />
          <Tab icon={<EditIcon />} label="Form Builder" />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            {forms.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No forms found for this event
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleNewForm}
                  disabled={loadingSave}
                  sx={{ mt: 2 }}
                >
                  Create Your First Form
                </Button>
              </Box>
            ) : (
              <List>
                {forms.map((form) => (
                  <React.Fragment key={form.id}>
                    <ListItem 
                      button 
                      onClick={() => handleSelectForm(form)}
                      secondaryAction={
                        <Box>
                          <IconButton 
                            edge="end" 
                            aria-label="edit" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectForm(form);
                            }}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteForm(form);
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText 
                        primary={form.title || 'Untitled Form'} 
                        secondary={form.description || 'No description'}
                        primaryTypographyProps={{
                          fontWeight: form.id === selectedForm?.id ? 'bold' : 'normal'
                        }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}

        {activeTab === 1 && selectedForm && (
          <Box>
            <TextField
              fullWidth
              label="Form Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              margin="normal"
              variant="outlined"
              required
            />
            <TextField
              fullWidth
              label="Form Description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              margin="normal"
              variant="outlined"
              multiline
              rows={2}
            />
            
            <Box>
              {selectedForm ? (
                <FormBuilder
                  key={selectedForm.id || 'new-form'}
                  eventId={eventId}
                  initialForm={{
                    ...selectedForm,
                    title: formTitle,
                    description: formDescription
                  }}
                  onSave={handleSaveForm}
                  onPublish={handleSaveForm}
                  onChange={(updatedForm) => {
                    // console.log('Form updated:', updatedForm);
                    setSelectedForm(updatedForm);
                  }}
                />
              ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  No form selected. Please create a new form or select an existing one.
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Form</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this form? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button 
            onClick={confirmDeleteForm} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FormBuilderPage;
